import React, { useMemo, useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import useRemoveTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useRemoveTask.v3";
import useUpdateTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useUpdateTask.v3";
import { TaskDetails } from "@fartherfinance/frontend/api/TaskWorkflows/Types";

import { useTaskPermissions } from "../../Tasks/taskPermissionsContext";
import { useTasksNavigation } from "../hooks";
import TaskMessageModal from "../TaskMessageModal/TaskMessageModal";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import Menu from "@src/sharedComponents/Menu/Menu";
import Text from "@src/sharedComponents/Typography/Text";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./TaskWorkflowActions.module.css";

interface TaskWorkflowActionsProps {
  taskDetails: TaskDetails;
}

const TaskWorkflowActions: React.FC<TaskWorkflowActionsProps> = ({
  taskDetails,
}) => {
  const [isSwitchToTaskModalVisible, setIsSwitchToTaskModalVisible] =
    useState(false);
  const [isDeleteTaskModalVisible, setIsDeleteTaskModalVisible] =
    useState(false);
  const [isMutating, setIsMutating] = useState(false);

  const auth = useAdvisorRequestAuth();
  const callUpdateTaskV3 = useUpdateTaskV3(auth);
  const callRemoveTaskV3 = useRemoveTaskV3(auth);
  const statusNotification = useStatusNotification();
  const { handleNavigateToTasksList } = useTasksNavigation();

  const { taskPermissions } = useTaskPermissions();
  const { isAllowedToDelete: isAllowedToDeleteTask } =
    taskPermissions(taskDetails);

  const isSubtask = Boolean(taskDetails.parent);

  const handleTurnSubtaskIntoTask = async (): Promise<void> => {
    try {
      setIsMutating(true);

      await callUpdateTaskV3({
        taskId: taskDetails.id,
        parentTaskId: taskDetails.parent?.id,
        request: { parentId: { value: null } },
      });

      setIsSwitchToTaskModalVisible(false);
      trackEvent({ name: "Advisor Tasks Update" });
      statusNotification("Task updated successfully.", "Success");
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to update task."),
        "Error"
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleDeleteTask = async (): Promise<void> => {
    try {
      setIsMutating(true);

      await callRemoveTaskV3(taskDetails.id);

      setIsDeleteTaskModalVisible(false);
      trackEvent({ name: "Advisor Tasks Delete" });
      statusNotification("Task deleted successfully.", "Success");
      handleNavigateToTasksList();
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to delete task."),
        "Error"
      );
      setIsMutating(false);
    }
  };

  const taskMenuOptions = useMemo(
    () => [
      ...(isSubtask
        ? [
            {
              label: "Switch to Task",
              isCloseOnClick: true,
              onClick: () => setIsSwitchToTaskModalVisible(true),
            },
          ]
        : []),
      {
        label: "Delete",
        className: styles.delete,
        isDisabled: !isAllowedToDeleteTask,
        isCloseOnClick: true,
        onClick: () => setIsDeleteTaskModalVisible(true),
      },
    ],
    [isAllowedToDeleteTask, isSubtask]
  );

  return (
    <>
      <Menu options={taskMenuOptions}>
        {({ handleOpenMenu }) => (
          <IconButton
            onClick={handleOpenMenu}
            iconClassName={styles.menuIcon}
            IconComponent={MoreHorizIcon}
          />
        )}
      </Menu>

      <TaskMessageModal
        title="Switch To Task"
        isOpen={isSwitchToTaskModalVisible}
        onClose={{
          label: "Cancel",
          action: () => setIsSwitchToTaskModalVisible(false),
        }}
        onConfirm={{
          label: "Save",
          action: handleTurnSubtaskIntoTask,
          isDisabled: isMutating,
        }}
      >
        <Text variant="body" color="textSubtle">
          Are you sure you want to change the "{taskDetails.fields.title}"
          subtask to a task?
        </Text>
      </TaskMessageModal>

      <TaskMessageModal
        title="Delete Task?"
        isOpen={isDeleteTaskModalVisible}
        onClose={{
          label: "Cancel",
          action: () => setIsDeleteTaskModalVisible(false),
        }}
        onConfirm={{
          label: "Delete",
          action: handleDeleteTask,
          isDisabled: isMutating,
        }}
      >
        <Text variant="body" color="textSubtle">
          By proceeding you will permanently delete the task "
          {taskDetails.fields.title}".
        </Text>
      </TaskMessageModal>
    </>
  );
};

export default TaskWorkflowActions;
